import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { CustomerSession, OtpVerifyResponse } from '../../shared/types'
import {
  bootstrapSession,
  clearSession,
  getSession,
  hasSessionHint,
  logoutServerSession,
  setSession,
  setUnauthorizedHandler,
} from '../../shared/api/client'
import { verifyOtp } from '../../shared/api/account'

interface AuthContextValue {
  session: CustomerSession | null
  isAuthenticated: boolean
  /** True while the boot silent refresh is restoring a previous login — gate redirects on it. */
  initializing: boolean
  /**
   * Verify an SMS code (D-083/D-084); adopts the session when the phone is a customer, and returns
   * the full result so the caller can seed a staff session when the phone resolved to STAFF.
   */
  verifyOtp: (phone: string, code: string, name?: string) => Promise<OtpVerifyResponse>
  /** Replace the current session (e.g. after a profile edit returns a refreshed session). */
  updateSession: (s: CustomerSession) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Holds the customer session (mirrored from the in-memory store, D-053) and the auth actions.
 * On mount it runs one silent refresh so a previous login survives page reloads — the access
 * token itself lives only in memory; the long session is the httpOnly refresh cookie.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<CustomerSession | null>(() => getSession())
  const [initializing, setInitializing] = useState<boolean>(() => getSession() === null && hasSessionHint())
  const queryClient = useQueryClient()

  const adopt = useCallback((s: CustomerSession) => {
    setSession(s)
    setSessionState(s)
  }, [])

  // Boot: restore the login via the refresh cookie (no-op for guests — no hint, no call).
  useEffect(() => {
    let active = true
    void bootstrapSession().then((restored) => {
      if (active) {
        if (restored) {
          setSessionState(restored)
        }
        setInitializing(false)
      }
    })
    return () => {
      active = false
    }
  }, [])

  // When a customer API call 401s and the silent refresh couldn't rescue it, the session is
  // truly gone — drop it here too so the account page redirects to login instead of looping
  // 401s (guest browsing is unaffected).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession()
      setSessionState(null)
      queryClient.removeQueries({ queryKey: ['account'] })
    })
    return () => setUnauthorizedHandler(null)
  }, [queryClient])

  const value = useMemo<AuthContextValue>(() => {
    const doVerifyOtp = async (phone: string, code: string, name?: string): Promise<OtpVerifyResponse> => {
      const res = await verifyOtp(phone, code, name)
      if (res.realm === 'CUSTOMER' && res.customer) {
        adopt(res.customer)
      }
      // STAFF is seeded by the caller (LoginPage) into the separate owner session store — the
      // customer context deliberately doesn't reach across realms.
      return res
    }
    const logout = () => {
      // Revoke the server-side session line (refresh-token family + cookie); best-effort —
      // the local session is gone either way.
      void logoutServerSession().catch(() => undefined)
      clearSession()
      setSessionState(null)
      // Drop any cached account data so the next customer doesn't see the previous one's.
      queryClient.removeQueries({ queryKey: ['account'] })
    }
    return { session, isAuthenticated: !!session, initializing, verifyOtp: doVerifyOtp, updateSession: adopt, logout }
  }, [session, initializing, adopt, queryClient])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
